import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface ImagePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string | null | undefined;
  altText?: string;
}

export default function ImagePreviewModal({ isOpen, onClose, imageUrl, altText = 'Preview' }: ImagePreviewModalProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  // Reset loading and error states when modal opens or image changes
  useEffect(() => {
    if (isOpen && imageUrl) {
      setIsLoading(true);
      setHasError(false);
    }
  }, [isOpen, imageUrl]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleImageLoad = () => {
    // Image loaded successfully
    setIsLoading(false);
    setHasError(false);
  };

  const handleImageError = () => {
    // Failed to load image
    setIsLoading(false);
    setHasError(true);
  };

  // Don't render if there's no valid image URL
  if (!imageUrl) {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 cursor-pointer"
          />

          {/* Modal Container */}
          <div className="fixed inset-0 flex items-start justify-center z-[60] p-4 pt-16 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              className="relative max-w-[90vw] max-h-[90vh] pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute -top-12 right-0 p-2 text-white hover:text-secondary-300 bg-black/50 hover:bg-black/70 rounded-lg transition-colors"
                aria-label="Close preview"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>

              {/* Loading Spinner */}
              {isLoading && !hasError && (
                <div className="flex items-center justify-center w-[400px] h-[400px] bg-secondary-100 dark:bg-secondary-800 rounded-lg">
                  <div className="flex flex-col items-center space-y-3">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                    <p className="text-secondary-600 dark:text-secondary-400 text-sm">Loading image...</p>
                  </div>
                </div>
              )}

              {/* Error State */}
              {hasError && (
                <div className="flex items-center justify-center w-[400px] h-[400px] bg-secondary-100 dark:bg-secondary-800 rounded-lg">
                  <div className="flex flex-col items-center space-y-3 p-6 text-center">
                    <ExclamationTriangleIcon className="w-16 h-16 text-warning-500" />
                    <p className="text-secondary-900 dark:text-white font-medium">Failed to load image</p>
                    <p className="text-secondary-600 dark:text-secondary-400 text-sm">
                      The image could not be loaded. It may have been deleted or the file path is invalid.
                    </p>
                    <p className="text-secondary-500 dark:text-secondary-500 text-xs font-mono break-all max-w-md">
                      {imageUrl}
                    </p>
                  </div>
                </div>
              )}

              {/* Image */}
              <img
                src={imageUrl}
                alt={altText}
                onLoad={handleImageLoad}
                onError={handleImageError}
                className={`max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl ${isLoading || hasError ? 'hidden' : ''
                  }`}
              />

              {/* Optional: Image caption */}
              {altText && (
                <div className="absolute -bottom-12 left-0 right-0 text-center">
                  <p className="text-white text-sm bg-black/50 px-4 py-2 rounded-lg inline-block">
                    {altText}
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
